import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import {
    CheckCircle,
    XCircle,
    AlertTriangle,
    TrendingUp,
    Clock,
    Calendar,
} from 'lucide-react'
import { Typogaphy } from 'vnetwork-ui'

import Paper from '@/components/Paper'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    getServiceUptimeStats,
    getServiceMonthlySummary,
} from '@/modules/Service/services/sla'
import { formatMinutes } from '../../Shared/utils/format-value'

interface UptimeStatsProps {
    serviceId: number
}

const PERIOD_OPTIONS = [
    { value: 'this_month', label: 'Tháng này' },
    { value: 'last_month', label: 'Tháng trước' },
    { value: 'last_3_months', label: '3 tháng gần nhất' },
    { value: 'this_year', label: 'Năm nay' },
]

interface PeriodDates {
    start: string
    end: string
    year: number
    month: number
}

const getPeriodDates = (period: string): PeriodDates => {
    const now = dayjs()
    switch (period) {
        case 'this_month':
            return {
                start: now.startOf('month').format('YYYY-MM-DD'),
                end: now.endOf('month').format('YYYY-MM-DD'),
                year: now.year(),
                month: now.month() + 1,
            }
        case 'last_month': {
            const lastMonth = now.subtract(1, 'month')
            return {
                start: lastMonth.startOf('month').format('YYYY-MM-DD'),
                end: lastMonth.endOf('month').format('YYYY-MM-DD'),
                year: lastMonth.year(),
                month: lastMonth.month() + 1,
            }
        }
        case 'last_3_months':
            return {
                start: now
                    .subtract(3, 'month')
                    .startOf('month')
                    .format('YYYY-MM-DD'),
                end: now.endOf('month').format('YYYY-MM-DD'),
                year: now.year(),
                month: now.month() + 1,
            }
        case 'this_year':
            return {
                start: now.startOf('year').format('YYYY-MM-DD'),
                end: now.endOf('year').format('YYYY-MM-DD'),
                year: now.year(),
                month: now.month() + 1,
            }
        default:
            return {
                start: now.startOf('month').format('YYYY-MM-DD'),
                end: now.endOf('month').format('YYYY-MM-DD'),
                year: now.year(),
                month: now.month() + 1,
            }
    }
}

const UptimeStats: React.FC<UptimeStatsProps> = ({ serviceId }) => {
    const { t } = useTranslation('services')
    const [period, setPeriod] = useState('this_month')

    const { start, end, year, month } = getPeriodDates(period)

    // Fetch uptime stats
    const { data: statsData, isLoading: isLoadingStats } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SLA_UPTIME_STATS, serviceId, start, end],
        queryFn: () => getServiceUptimeStats(serviceId, start, end),
    })

    // Fetch monthly summary for trend
    const { data: summaryData, isLoading: isLoadingSummary } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.SLA_MONTHLY_SUMMARY, serviceId, year, month],
            queryFn: () => getServiceMonthlySummary(serviceId, year, month),
            enabled: period === 'last_3_months' || period === 'this_year',
        })

    const stats = statsData?.result
    const monthlySummary = summaryData?.results || []

    const isLoading = isLoadingStats || isLoadingSummary

    if (isLoading) {
        return (
            <Paper className="p-4">
                <div className="flex items-center justify-between mb-4">
                    <Skeleton className="h-6 w-32" />
                    <Skeleton className="h-9 w-40" />
                </div>
                <div className="grid grid-cols-4 gap-3">
                    {[1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} className="h-24 rounded-lg" />
                    ))}
                </div>
            </Paper>
        )
    }

    if (!stats) {
        return (
            <Paper className="p-4">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-blue-500" />
                        <Typogaphy className="font-semibold text-gray-900">
                            {t('sla.uptime_stats', 'Thống kê Uptime')}
                        </Typogaphy>
                    </div>
                    <Select value={period} onValueChange={setPeriod}>
                        <SelectTrigger className="w-40">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {PERIOD_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="text-center py-6 bg-gray-50 rounded-lg">
                    <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">
                        {t(
                            'sla.no_stats_data',
                            'Chưa có dữ liệu thống kê cho khoảng thời gian này'
                        )}
                    </p>
                </div>
            </Paper>
        )
    }

    return (
        <Paper className="p-4">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-500" />
                    <Typogaphy className="font-semibold text-gray-900">
                        {t('sla.uptime_stats', 'Thống kê Uptime')}
                    </Typogaphy>
                    {stats.sla_met ? (
                        <Badge className="bg-green-100 text-green-700 border-green-200">
                            Đạt SLA
                        </Badge>
                    ) : (
                        <Badge className="bg-red-100 text-red-700 border-red-200">
                            Vi phạm SLA
                        </Badge>
                    )}
                </div>
                <Select value={period} onValueChange={setPeriod}>
                    <SelectTrigger className="w-40">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {PERIOD_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {/* Actual Uptime */}
                <div
                    className={`p-3 rounded-lg ${
                        stats.sla_met ? 'bg-green-50' : 'bg-red-50'
                    }`}
                >
                    <div
                        className={`flex items-center gap-1 mb-1 ${
                            stats.sla_met ? 'text-green-600' : 'text-red-600'
                        }`}
                    >
                        {stats.sla_met ? (
                            <CheckCircle className="w-4 h-4" />
                        ) : (
                            <XCircle className="w-4 h-4" />
                        )}
                        <span className="text-xs">Actual Uptime</span>
                    </div>
                    <span
                        className={`text-2xl font-bold ${
                            stats.sla_met ? 'text-green-700' : 'text-red-700'
                        }`}
                    >
                        {stats.actual_uptime?.toFixed(2)}%
                    </span>
                </div>

                {/* Target Uptime */}
                <div className="p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-1 text-blue-600 mb-1">
                        <TrendingUp className="w-4 h-4" />
                        <span className="text-xs">Target</span>
                    </div>
                    <span className="text-2xl font-bold text-blue-700">
                        {stats.target_uptime}%
                    </span>
                </div>

                {/* Total Downtime */}
                <div className="p-3 bg-orange-50 rounded-lg">
                    <div className="flex items-center gap-1 text-orange-600 mb-1">
                        <Clock className="w-4 h-4" />
                        <span className="text-xs">Downtime</span>
                    </div>
                    <span className="text-2xl font-bold text-orange-700">
                        {formatMinutes(stats.total_downtime_minutes)}
                    </span>
                    <span className="text-xs text-orange-600 block">
                        / {formatMinutes(stats.allowed_downtime)} allowed
                    </span>
                </div>

                {/* Incidents */}
                <div className="p-3 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-1 text-purple-600 mb-1">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-xs">Incidents</span>
                    </div>
                    <span className="text-2xl font-bold text-purple-700">
                        {stats.incident_count}
                    </span>
                </div>
            </div>

            {/* Monthly Trend (if applicable) */}
            {monthlySummary.length > 0 && (
                <div className="border-t pt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                        {t('sla.monthly_trend', 'Xu hướng theo tháng')}
                    </h4>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                        {monthlySummary.map((month) => (
                            <div
                                key={month.month}
                                className={`p-2 rounded-lg text-center ${
                                    month.sla_met ? 'bg-green-50' : 'bg-red-50'
                                }`}
                            >
                                <div className="text-xs text-gray-500 mb-1">
                                    {dayjs(month.month).format('MM/YYYY')}
                                </div>
                                <div
                                    className={`text-sm font-bold ${
                                        month.sla_met
                                            ? 'text-green-700'
                                            : 'text-red-700'
                                    }`}
                                >
                                    {month.actual_uptime?.toFixed(1)}%
                                </div>
                                <div className="text-xs text-gray-400">
                                    {month.incident_count} incidents
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </Paper>
    )
}

export default UptimeStats
